import emailjs from '@emailjs/browser';

// EmailJS configuration from environment variables
const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_flashcheck';
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_flashcheck_request';  
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'your_public_key_here';
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@yourdomain.com';

// Check if EmailJS is properly configured
const isEmailJSConfigured = () => {
  return EMAILJS_SERVICE_ID !== 'service_flashcheck' && 
         EMAILJS_TEMPLATE_ID !== 'template_flashcheck_request' && 
         EMAILJS_PUBLIC_KEY !== 'your_public_key_here' &&
         EMAILJS_SERVICE_ID && 
         EMAILJS_TEMPLATE_ID && 
         EMAILJS_PUBLIC_KEY;
};

export interface EmailData {
  customerEmail: string;
  customerDomain: string;
  requestedDomain: string;
  message: string;
  timestamp: string;
}

export const sendFlashCheckRequest = async (data: EmailData): Promise<void> => {
  try {
    const useEmailJS = isEmailJSConfigured();

    if (useEmailJS) {
      // Initialize EmailJS with the public key
      emailjs.init(EMAILJS_PUBLIC_KEY);

      const templateParams = {
        to_email: SUPPORT_EMAIL,
        customer_email: data.customerEmail,
        customer_domain: data.customerDomain,
        requested_domain: data.requestedDomain,
        message: data.message || 'No additional message provided',
        timestamp: data.timestamp,
        formatted_timestamp: new Date(data.timestamp).toLocaleString(),
        has_message: data.message ? 'Yes' : 'No',
        request_summary: `FlashCheck access request for ${data.requestedDomain} from ${data.customerEmail}`,
      };

      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      if (result.status !== 200) {
        throw new Error(`EmailJS returned status: ${result.status} - ${result.text || 'Unknown error'}`);
      }
    } else {
      // Fallback to mailto with formatted content
      const subject = encodeURIComponent(`FlashCheck Request from ${data.customerEmail}`);
      const emailBody = `
FlashCheck Access Request

Customer Details:
- Email: ${data.customerEmail}
- Domain: ${data.customerDomain}

Request:
- Requested Domain: ${data.requestedDomain}
- Message: ${data.message || 'No additional message provided'}

Timestamp: ${data.timestamp}

Please review this request and grant FlashCheck access for the specified domain.
      `;
      const body = encodeURIComponent(emailBody);
      const mailtoLink = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
      window.open(mailtoLink);
    }
  } catch (error: any) {
    // Log error for debugging but don't expose sensitive details to user
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to send FlashCheck request:', error);
      console.error('Error details:', {
        status: error.status,
        text: error.text,
        message: error.message,
      });
    }
    
    // Provide user-friendly error messages
    let errorMessage = 'Failed to send request. Please try again.';
    
    if (error.status === 400) {
      errorMessage = 'Invalid request format. Please try again or contact support.';
    } else if (error.status === 401) {
      errorMessage = 'Authentication failed. Please contact support.';
    } else if (error.status === 404) {
      errorMessage = 'Email service temporarily unavailable. Please try again later.';
    } else if (error.status === 402) {
      errorMessage = 'Email service quota exceeded. Please contact support.';
    } else if (error.text && process.env.NODE_ENV === 'development') {
      errorMessage = `Service Error: ${error.text}`;
    }
    
    throw new Error(errorMessage);
  }
};


